'use client';

import React, { useState } from 'react';
import { CreditCard, ShieldCheck, Percent, Store, Sparkles, ChevronDown, ChevronUp, Key, Check, Copy, PlayCircle } from 'lucide-react';

interface MercadoPagoHeroCardProps {
  connected: boolean;
  publicKey: string;
  accessToken: string;
  hasWebhookSecret?: boolean;
  onToggleConnection: () => void;
  onSaveCredentials: (pubKey: string, accToken: string, webhookSecret?: string) => Promise<void>;
}

export function MercadoPagoHeroCard({
  connected,
  publicKey,
  accessToken,
  hasWebhookSecret,
  onToggleConnection,
  onSaveCredentials
}: MercadoPagoHeroCardProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputPubKey, setInputPubKey] = useState(publicKey);
  const [inputAccToken, setInputAccToken] = useState(accessToken);
  const [inputWebhookSecret, setInputWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const webhookUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/webhooks/mercadopago`
    : `${process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3009'}/api/webhooks/mercadopago`;

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSaveCredentials(inputPubKey, inputAccToken, inputWebhookSecret || undefined);
      alert('¡Credenciales de Mercado Pago guardadas con éxito!');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/payments/test-webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ simulate: true })
      });
      const data = await res.json();
      if (data.success) {
        setTestResult(`✅ Webhook verificado correctamente: Firma HMAC válida, estado "${data.status}" e idempotencia garantizada.`);
      } else {
        setTestResult(`❌ Error en prueba de webhook: ${data.error}`);
      }
    } catch (err: any) {
      setTestResult(`❌ Error de conexión al probar webhook: ${err.message}`);
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div className="bg-[#081d37] text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden space-y-6">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#009ee3]/20 border border-[#009ee3]/40 flex items-center justify-center text-[#009ee3] shadow-inner">
            <CreditCard className="w-6 h-6" />
          </div>
          <Sparkles className="w-5 h-5 text-sky-400" />
        </div>

        <span
          className={`text-[11px] font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${
            connected
              ? 'bg-emerald-950/80 text-emerald-400 border-emerald-800'
              : 'bg-white/10 text-white/80 border-white/20'
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full ${
              connected ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
            }`}
          />
          {connected ? 'Conectado (Modo Pruebas / Sandbox)' : 'Sin conectar'}
        </span>
      </div>

      {/* Title & Subtitle */}
      <div>
        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight">
          Recibí pagos en tu cuenta <br />
          <span className="text-[#009ee3]">de MercadoPago</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 mt-2 leading-relaxed max-w-xl">
          Integración oficial con Webhooks firmados (HMAC-SHA256). Acreditación atómica de señas sin intermediarios.
        </p>
      </div>

      {/* 3 Benefit Pills */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
        <div className="p-3.5 bg-white/5 border border-white/10 rounded-2xl space-y-1">
          <div className="flex items-center gap-2 text-sky-300">
            <Store className="w-4 h-4" />
            <h4 className="text-xs font-bold">A tu cuenta de MercadoPago</h4>
          </div>
          <p className="text-[11px] text-slate-400 leading-snug">
            El dinero entra directo a tu cuenta de Mercado Pago.
          </p>
        </div>

        <div className="p-3.5 bg-white/5 border border-white/10 rounded-2xl space-y-1">
          <div className="flex items-center gap-2 text-sky-300">
            <ShieldCheck className="w-4 h-4" />
            <h4 className="text-xs font-bold">Webhooks Firmados</h4>
          </div>
          <p className="text-[11px] text-slate-400 leading-snug">
            Validación de firma x-signature e idempotencia garantizada.
          </p>
        </div>

        <div className="p-3.5 bg-white/5 border border-white/10 rounded-2xl space-y-1">
          <div className="flex items-center gap-2 text-sky-300">
            <Percent className="w-4 h-4" />
            <h4 className="text-xs font-bold">Costos de transacción</h4>
          </div>
          <p className="text-[11px] text-slate-400 leading-snug">
            Comisiones directas de MP según tarifas oficiales vigentes.
          </p>
        </div>
      </div>

      {/* URL de Webhook y Botón de Prueba */}
      <div className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <span className="text-[10px] font-bold text-sky-300 uppercase tracking-wider block">
              URL Oficial de Notificaciones Webhook (Mercado Pago Developers)
            </span>
            <code className="text-xs font-mono text-slate-200 break-all select-all">
              {webhookUrl}
            </code>
          </div>
          <button
            type="button"
            onClick={handleCopyWebhookUrl}
            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 transition self-start sm:self-auto cursor-pointer"
          >
            {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedUrl ? 'Copiado' : 'Copiar URL'}</span>
          </button>
        </div>

        <div className="pt-1 flex items-center justify-between border-t border-white/10">
          <span className="text-[11px] text-slate-400">
            Verificación de firma criptográfica: {hasWebhookSecret ? '🔒 Secreto configurado' : '⚡ Clave predeterminada de sandbox'}
          </span>
          <button
            type="button"
            disabled={testingWebhook}
            onClick={handleTestWebhook}
            className="text-xs font-bold text-sky-400 hover:text-sky-300 flex items-center gap-1 transition cursor-pointer disabled:opacity-50"
          >
            <PlayCircle className="w-3.5 h-3.5" />
            <span>{testingWebhook ? 'Probando...' : 'Simular Webhook firmado'}</span>
          </button>
        </div>

        {testResult && (
          <div className="p-2.5 bg-slate-900/90 border border-slate-700 rounded-xl text-xs font-medium text-slate-200 mt-2">
            {testResult}
          </div>
        )}
      </div>

      {/* Action Row */}
      <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-300 text-center sm:text-left">
          {connected
            ? '✅ Mercado Pago habilitado para cobro de señas con acreditación en PostgreSQL.'
            : 'Mercado Pago deshabilitado hasta conectar.'}
        </p>

        <button
          type="button"
          onClick={onToggleConnection}
          className={`px-6 py-3 rounded-2xl font-bold text-xs transition-all shadow-md shrink-0 cursor-pointer ${
            connected
              ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
              : 'bg-[#009ee3] hover:bg-[#0081bb] text-white shadow-sky-500/20'
          }`}
        >
          {connected ? 'Desconectar cuenta' : 'Conectar ahora'}
        </button>
      </div>

      {/* Manual / Advanced Credentials Expander */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs text-slate-400 hover:text-sky-300 transition-colors flex items-center gap-1.5 cursor-pointer"
        >
          <Key className="w-3.5 h-3.5" />
          <span>Configuración de credenciales de comercio (Multitenant)</span>
          {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {showAdvanced && (
          <form onSubmit={handleSave} className="mt-4 p-5 bg-white/5 border border-white/10 rounded-2xl space-y-3.5 animate-in fade-in">
            <p className="text-xs text-slate-300">
              Cada negocio puede registrar sus credenciales oficiales de Mercado Pago Developers:
            </p>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">
                Public Key
              </label>
              <input
                type="text"
                placeholder="APP_USR-..."
                value={inputPubKey}
                onChange={(e) => setInputPubKey(e.target.value)}
                className="w-full px-3.5 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-[#009ee3]"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">
                Access Token
              </label>
              <input
                type="password"
                placeholder={accessToken ? '••••••••••••••••' : 'APP_USR-... o TEST-...'}
                value={inputAccToken}
                onChange={(e) => setInputAccToken(e.target.value)}
                className="w-full px-3.5 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-[#009ee3]"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Por seguridad, los secretos nunca se exhiben en texto plano en la interfaz.
              </span>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">
                Webhook Secret (Firma HMAC-SHA256)
              </label>
              <input
                type="password"
                placeholder={hasWebhookSecret ? '•••••••••••••••• (Secreto activo)' : 'Secreto de firma de webhook'}
                value={inputWebhookSecret}
                onChange={(e) => setInputWebhookSecret(e.target.value)}
                className="w-full px-3.5 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-[#009ee3]"
              />
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-[#009ee3] hover:bg-[#0081bb] text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
              >
                {saving ? 'Guardando...' : 'Guardar credenciales'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
